import { useEffect, useRef } from 'react';

export interface NewCheckboxProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type' | 'size'> {
  label?: React.ReactNode;
  labelClassName?: string;
  invalid?: boolean;
  indeterminate?: boolean;
  /** `'sm'` renders the 16×16 variant for compact rows (grid cells, card headers). Default 20×20. */
  size?: 'sm';
  /** Colours the label text Bombiix Blue (#6DC1F5); box chrome unchanged. */
  isHighlight?: boolean;
}

/**
 * NewCheckbox — Bombiix master-design checkbox (Figma node 5163:299549).
 * 20px square, 4px radius, white bg + #D8D8D8 border unchecked, Bombiix Green
 * fill + white tick when checked. Styling lives in _checkbox.scss under
 * master-design; component only wires classes + native input behaviour.
 * Optional label renders to the right (click toggles). `invalid` toggles the
 * red border + label, `indeterminate` drives the mixed-state dash.
 */
function NewCheckbox({ label, labelClassName, className, id, invalid, indeterminate, size, isHighlight, ...rest }: NewCheckboxProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const inputClass = ['bombiix-checkbox__input', className].filter(Boolean).join(' ');
  const labelClass = ['bombiix-checkbox__label', labelClassName].filter(Boolean).join(' ');
  const rootClass = [
    'bombiix-checkbox',
    size === 'sm' ? 'bombiix-checkbox--sm' : '',
    invalid ? 'bombiix-checkbox--invalid' : '',
    isHighlight ? 'bombiix-checkbox--bulk' : '',
  ]
    .filter(Boolean)
    .join(' ');

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.indeterminate = Boolean(indeterminate);
    }
  }, [indeterminate]);

  return (
    <label className={rootClass} htmlFor={id}>
      <input ref={inputRef} type="checkbox" id={id} className={inputClass} {...rest} />
      <span className="bombiix-checkbox__box" aria-hidden="true" />
      {label !== undefined ? <span className={labelClass}>{label}</span> : null}
    </label>
  );
}

export default NewCheckbox;
